import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCategoryName, getResourceById } from '@/lib/catalog';
import type { Resource } from '@/mocks/types';

const FEATURED_IDS = ['v-anime-shanhai', 's-clean', 't-js', 'v-doc-earth'];

const slides = FEATURED_IDS.map((id) => getResourceById(id)).filter(
  (item): item is Resource => Boolean(item),
);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((next: number) => {
    setIndex(((next % slides.length) + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (paused || slides.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused]);

  if (slides.length === 0) return null;
  const current = slides[index];

  return (
    <section className="w-full px-4 md:px-8 pt-10">
      <div
        className="relative w-full rounded-lg overflow-hidden bg-foreground-900"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="relative w-full h-[260px] sm:h-[340px] md:h-[460px]">
          {slides.map((slide, i) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <img
                src={slide.cover}
                alt={`${slide.title} - 焦点推荐`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/10"></div>
            </div>
          ))}

          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-10">
            <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium mb-3">
              <i className="ri-fire-line"></i>
              焦点推荐 · {getCategoryName(current.category)}
            </span>
            <h2 className="font-heading font-bold text-xl md:text-3xl text-background-50 max-w-2xl">
              {current.title}
            </h2>
            <p className="mt-2 text-sm md:text-base text-background-200 max-w-2xl line-clamp-2">
              {current.summary}
            </p>
            <div className="mt-4 flex items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                查看详情
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to={`/category/${current.category}`}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-background-50/15 hover:bg-background-50/25 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                更多{getCategoryName(current.category)}
              </Link>
            </div>
          </div>

          <button
            type="button"
            onClick={() => goTo(index - 1)}
            aria-label="上一张"
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-foreground-950/40 hover:bg-foreground-950/70 text-background-50 flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            type="button"
            onClick={() => goTo(index + 1)}
            aria-label="下一张"
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-foreground-950/40 hover:bg-foreground-950/70 text-background-50 flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>

          <div className="absolute bottom-4 right-5 md:bottom-6 md:right-10 flex items-center gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`切换到第 ${i + 1} 张`}
                className={`h-2 rounded-full transition-all cursor-pointer ${
                  i === index ? 'w-6 bg-accent-500' : 'w-2 bg-background-50/50 hover:bg-background-50/80'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}